import dayjs, { Dayjs } from 'dayjs'
import { TypeSignUpApplicationResponse } from '../Hooks'

export const isVerificationSuccessful = (
  verification: TypeSignUpApplicationResponse | undefined
) => {
  if (!verification) {
    return false
  }

  return (
    !verification.duplicateExists &&
    !verification.potentialSanctionedMatch &&
    !verification.potentialFraudMatch &&
    verification.isVerified
  )
}

const NOT_NUMBERS_AND_SLASHES = /[^\d/]/g
const MAX_AGE = 150

export function validateDateOfBirth(dateOfBirth: string, returnTheDateIfValid = false): string {
  if (!dateOfBirth) {
    return ''
  }

  dateOfBirth = dateOfBirth.replace(NOT_NUMBERS_AND_SLASHES, '')
  if (
    dateOfBirth.length < 10 ||
    dateOfBirth.indexOf('/') < 0 ||
    dateOfBirth.lastIndexOf('/') === dateOfBirth.indexOf('/')
  ) {
    return 'Please enter a valid date of birth.'
  }

  let eighteenYearsAgo: Date | Dayjs = new Date()
  eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18)
  eighteenYearsAgo.setHours(0)
  eighteenYearsAgo.setMinutes(0)
  eighteenYearsAgo.setSeconds(0)
  eighteenYearsAgo.setMilliseconds(0)
  eighteenYearsAgo = dayjs(eighteenYearsAgo)

  const birthYear = Number(dateOfBirth.substring(dateOfBirth.lastIndexOf('/') + 1))
  const birthDay = Number(dateOfBirth.substring(0, dateOfBirth.indexOf('/')))

  const birthMonth = Number(
    dateOfBirth.substring(dateOfBirth.indexOf('/') + 1, dateOfBirth.lastIndexOf('/'))
  )

  if (birthDay > 31 || birthDay < 1) {
    return 'Please enter a day of the month between 1 and 31.'
  }

  if (birthMonth > 12 || birthMonth < 1) {
    return 'Please enter a month between 1 and 12.'
  }

  const currentYear = dayjs().year()
  if (birthYear > currentYear || birthYear < currentYear - MAX_AGE) {
    return 'Please enter a valid date of birth.'
  }

  const birthDate = dayjs(new Date(birthYear, birthMonth - 1, birthDay))
  if (birthDate.date() !== birthDay) {
    // This can happen when invalid days are entered. E.g. 30/2/1996 or 31/11/2000.
    return 'Please enter a valid date.'
  }

  const is18 = birthDate.isSame(eighteenYearsAgo) || birthDate.isBefore(eighteenYearsAgo)
  if (!is18) {
    return 'You must be aged 18 or over to wager in Australia.'
  } else {
    if (returnTheDateIfValid) {
      return birthDate.format('YYYY-MM-DD')
    }

    return ''
  }
}

export function getFormattedDateOfBirth(
  enteredText: string,
  oldText: string,
  selectionStart: number | null
) {
  if (enteredText.length <= oldText.length && enteredText !== oldText) {
    // The user has deleted/replaced something, so just let them do it.
    return enteredText
  }

  if ((selectionStart || oldText.length) <= oldText.length) {
    // The user is typing in the middle of the string, so just let them do it.
    return enteredText
  }

  let numbersAndSlashesOnly = enteredText.replace(NOT_NUMBERS_AND_SLASHES, '')
  if (!numbersAndSlashesOnly.includes('/') && numbersAndSlashesOnly.length === 8) {
    // Once they've finished typing, insert the slashes in the correct places if there aren't any already.
    return `${numbersAndSlashesOnly.substring(0, 2)}/${numbersAndSlashesOnly.substring(
      2,
      4
    )}/${numbersAndSlashesOnly.substring(4)}`
  }

  if (numbersAndSlashesOnly.indexOf('/') === 1) {
    numbersAndSlashesOnly = '0' + numbersAndSlashesOnly
  }

  if (numbersAndSlashesOnly.lastIndexOf('/') === 4) {
    numbersAndSlashesOnly = `${numbersAndSlashesOnly.substring(
      0,
      3
    )}0${numbersAndSlashesOnly.substring(3)}`
  }

  return numbersAndSlashesOnly
}
